import {
  MEETING_DURATION_MAX,
  MEETING_DURATION_MIN,
  MEETING_LEVELS,
  MEETING_LEVEL_LABELS,
  MEETING_STATUS_LABELS,
  MEETING_TEXT_MAX,
  MEETING_TITLE_MAX,
  MEETING_TYPES,
  MEETING_TYPE_LABELS,
  formatDate,
  meetingInputSchema,
  meetingListSchema,
  meetingOptionsSchema,
  meetingSchema,
  meetingStatusInputSchema,
  type Meeting,
  type MeetingLevelName,
  type MeetingOptions,
  type MeetingStatusName,
  type MeetingTypeName,
  type NextMeetingInput,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight, FolderKanban, List, Lock, Plus, Users } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { TabList, tabPanelProps } from '@/components/ui/tabs';
import { CollaborationPanel } from '@/features/collaboration/CollaborationPanel';
import { CloseMeetingDialog } from '@/features/meetings/CloseMeeting';
import { CriticalAnalysisPanel } from '@/features/meetings/CriticalAnalysis';
import { MeetingMinutes } from '@/features/meetings/Minutes';
import {
  MONTH_NAMES,
  WEEKDAYS,
  localParts,
  monthGrid,
  monthRange,
  startsAtFrom,
} from '@/features/meetings/time';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const PAGE_SIZE = 20;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

const STATUS_LIGHT: Record<MeetingStatusName, TrafficLight> = {
  SCHEDULED: 'blue',
  IN_PROGRESS: 'yellow',
  CLOSED: 'green',
};

/** Recorte da lista (DEC-049): agendadas, pendentes (agendadas vencidas), em andamento, encerradas ou todas. */
type View = 'upcoming' | 'pending' | 'running' | 'closed' | 'all';
const VIEWS: { key: View; label: string }[] = [
  { key: 'upcoming', label: 'Agendadas' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'running', label: 'Em andamento' },
  { key: 'closed', label: 'Encerradas' },
  { key: 'all', label: 'Todas' },
];
const viewQuery: Record<View, Record<string, string>> = {
  upcoming: { status: 'SCHEDULED', pending: 'false', order: 'asc' },
  pending: { pending: 'true', order: 'asc' },
  running: { status: 'IN_PROGRESS', order: 'asc' },
  closed: { status: 'CLOSED', order: 'desc' },
  all: { order: 'desc' },
};

const when = (m: Pick<Meeting, 'startsAt' | 'durationMinutes'>) => {
  const { day, time } = localParts(m.startsAt);
  return `${formatDate(`${day}T12:00:00Z`)} às ${time} · ${m.durationMinutes} min`;
};
const statusOf = (m: Meeting) =>
  m.pending ? (
    <StatusBadge status="red" label="Pendente" />
  ) : (
    <StatusBadge status={STATUS_LIGHT[m.status]} label={MEETING_STATUS_LABELS[m.status]} />
  );

/**
 * Gestão › Reuniões (T-120b, RF-39; DEC-049): reuniões do projeto (análise crítica e outras) por nível, com
 * participantes, pauta e registro; lista com recortes e filtros, calendário do mês e detalhe com as ações.
 */
export function ReunioesPage({ analysisOnly = false }: { analysisOnly?: boolean } = {}) {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-5xl">
      {analysisOnly ? (
        // Gestão › Análise crítica (T-280; RF-49, PRD §21): só as reuniões de análise crítica, com a condução.
        <PageHeader
          title="Análise crítica"
          description="As reuniões de análise crítica do projeto: a pauta montada pelos desvios, a condução item a item, as decisões e a ata."
        />
      ) : (
        <PageHeader
          title="Reuniões"
          description="Análises críticas e demais reuniões do projeto: quem participa, a pauta e o registro do que foi tratado."
        />
      )}
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="As reuniões pertencem a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          {project.status !== 'ACTIVE' ? (
            <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
              <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              Projeto arquivado: as reuniões ficam disponíveis só para consulta.
            </p>
          ) : null}
          <MeetingsSection
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
            analysisOnly={analysisOnly}
          />
        </div>
      )}
    </PageContainer>
  );
}

function MeetingsSection({
  organizationId,
  projectId,
  readOnly,
  analysisOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
  /** Só as reuniões de análise crítica (a tela Gestão › Análise crítica): o tipo fica fixo. */
  analysisOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/meetings`;
  const [params, setParams] = useSearchParams();
  const mode: 'list' | 'calendar' = params.get('visao') === 'calendario' ? 'calendar' : 'list';
  const setMode = (next: 'list' | 'calendar') =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'calendar') out.set('visao', 'calendario');
        else out.delete('visao');
        return out;
      },
      { replace: true },
    );
  // ?lista=pendentes abre nas pendentes (link da Visão Geral — T-140b).
  const [view, setView] = useState<View>(() =>
    params.get('lista') === 'pendentes' ? 'pending' : 'upcoming',
  );
  const [type, setType] = useState<MeetingTypeName | ''>(analysisOnly ? 'CRITICAL_ANALYSIS' : '');
  const [level, setLevel] = useState<MeetingLevelName | ''>('');
  const [page, setPage] = useState(1);
  // Reunião aberta; o link da notificação (?reuniao=) já abre o detalhe.
  const [openId, setOpenId] = useState<string | null>(() => {
    const raw = params.get('reuniao') ?? '';
    return UUID.test(raw) ? raw : null;
  });
  const [editing, setEditing] = useState<Meeting | 'new' | null>(null);
  /** Ao editar a partir do detalhe, o detalhe volta a abrir depois (com o registro digitado). */
  const [returnTo, setReturnTo] = useState<string | null>(null);
  /** Registro digitado e ainda não salvo, por reunião (não se perde ao sair para editar — pendência da T-120b). */
  const drafts = useRef(new Map<string, string>()).current;
  // Lida a reunião do link, ela sai do endereço (trocar de projeto ou recarregar não reabre).
  useEffect(() => {
    if (!params.has('reuniao')) return;
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete('reuniao');
        return out;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const finishEditing = () => {
    setEditing(null);
    if (returnTo) setOpenId(returnTo);
    setReturnTo(null);
  };
  const ids = { type: useId(), level: useId() };

  const options = useQuery({
    queryKey: ['meetings', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: meetingOptionsSchema, signal }),
    enabled: can('reunioes-decisoes', 'create') || can('reunioes-decisoes', 'edit'),
  });
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    ...viewQuery[view],
    ...(type ? { type } : {}),
    ...(level ? { level } : {}),
  });
  const list = useQuery({
    queryKey: ['meetings', projectId, 'list', query.toString()],
    queryFn: ({ signal }) =>
      client.request(`${base}?${query.toString()}`, { schema: meetingListSchema, signal }),
    placeholderData: keepPreviousData,
    enabled: mode === 'list',
  });
  const totalPages = list.data?.totalPages;
  useEffect(() => {
    if (totalPages === undefined) return;
    const last = Math.max(1, totalPages);
    if (page > last) setPage(last);
  }, [page, totalPages]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['meetings', projectId] });

  return (
    <section
      aria-label={analysisOnly ? 'Análises críticas do projeto' : 'Reuniões do projeto'}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          className="flex gap-1 rounded-md border border-border p-1"
          role="group"
          aria-label="Visualização"
        >
          <Button
            size="sm"
            variant={mode === 'list' ? 'primary' : 'ghost'}
            aria-pressed={mode === 'list'}
            onClick={() => setMode('list')}
          >
            <List aria-hidden />
            Lista
          </Button>
          <Button
            size="sm"
            variant={mode === 'calendar' ? 'primary' : 'ghost'}
            aria-pressed={mode === 'calendar'}
            onClick={() => setMode('calendar')}
          >
            <CalendarDays aria-hidden />
            Calendário
          </Button>
        </div>
        {can('reunioes-decisoes', 'create') ? (
          <Button onClick={() => setEditing('new')}>
            <Plus aria-hidden />
            Agendar reunião
          </Button>
        ) : null}
      </div>

      {mode === 'list' ? (
        <>
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Quais reuniões mostrar">
              {VIEWS.map((v) => (
                <Button
                  key={v.key}
                  size="sm"
                  variant={view === v.key ? 'primary' : 'outline'}
                  aria-pressed={view === v.key}
                  onClick={() => {
                    setView(v.key);
                    setPage(1);
                  }}
                >
                  {v.label}
                </Button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {analysisOnly ? null : (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor={ids.type} className="text-sm font-medium">
                    Tipo
                  </label>
                  <NativeSelect
                    id={ids.type}
                    value={type}
                    onChange={(e) => {
                      setType(e.target.value as MeetingTypeName | '');
                      setPage(1);
                    }}
                  >
                    <option value="">Todos</option>
                    {MEETING_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {MEETING_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <label htmlFor={ids.level} className="text-sm font-medium">
                  Nível
                </label>
                <NativeSelect
                  id={ids.level}
                  value={level}
                  onChange={(e) => {
                    setLevel(e.target.value as MeetingLevelName | '');
                    setPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  {MEETING_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {MEETING_LEVEL_LABELS[l]}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>
          </div>
          {list.isPending ? (
            <LoadingState rows={4} label="Carregando as reuniões" />
          ) : list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
          ) : list.data.items.length === 0 ? (
            <EmptyState
              className="rounded-lg border border-dashed border-border py-12"
              icon={CalendarDays}
              title={view === 'pending' ? 'Nenhuma reunião pendente' : 'Nenhuma reunião aqui'}
              description={
                view === 'pending'
                  ? 'Reunião agendada cujo horário passou sem ser iniciada aparece aqui para ser conduzida ou remarcada.'
                  : 'Agende as análises críticas e as demais reuniões do projeto: quem participa, a pauta e, depois, o registro.'
              }
            />
          ) : (
            <>
              <p className="text-sm text-muted-foreground" aria-live="polite">
                {list.data.total === 1 ? '1 reunião' : `${list.data.total} reuniões`}
                {list.isFetching ? ' · atualizando…' : ''}
              </p>
              <ul aria-label="Reuniões" className="flex flex-col gap-3">
                {list.data.items.map((m) => (
                  <li key={m.id} className="rounded-lg border border-border bg-surface p-4">
                    <article aria-label={m.title} className="flex flex-col gap-2 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-semibold uppercase">{MEETING_TYPE_LABELS[m.type]}</span>
                          <span>{MEETING_LEVEL_LABELS[m.level]}</span>
                          {statusOf(m)}
                        </p>
                        <h2 className="mt-1 text-base font-bold break-words">{m.title}</h2>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {when(m)}
                          {m.department ? ` · ${m.department.name}` : ''}
                          {m.businessUnit ? ` · ${m.businessUnit.name}` : ''}
                        </p>
                        <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                          <Users className="size-4" aria-hidden />
                          {m.participants.length === 1
                            ? '1 participante'
                            : `${m.participants.length} participantes`}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOpenId(m.id)}
                        aria-label={`Abrir a reunião ${m.title}`}
                      >
                        Abrir
                      </Button>
                    </article>
                  </li>
                ))}
              </ul>
              {list.data.totalPages > 1 ? (
                <nav aria-label="Paginação de reuniões" className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Página {list.data.page} de {list.data.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((x) => x - 1)}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= list.data.totalPages}
                      onClick={() => setPage((x) => x + 1)}
                    >
                      Próxima
                    </Button>
                  </div>
                </nav>
              ) : null}
            </>
          )}
        </>
      ) : (
        <MeetingCalendar
          base={base}
          projectId={projectId}
          onOpen={setOpenId}
          type={analysisOnly ? 'CRITICAL_ANALYSIS' : undefined}
        />
      )}

      <MeetingDetail
        base={base}
        organizationId={organizationId}
        projectId={projectId}
        meetingId={openId}
        readOnly={readOnly}
        canDelete={can('reunioes-decisoes', 'delete')}
        canCreate={can('reunioes-decisoes', 'create')}
        onOpenMeeting={setOpenId}
        drafts={drafts}
        onClose={() => setOpenId(null)}
        onEdit={(m) => {
          setOpenId(null);
          setReturnTo(m.id);
          setEditing(m);
        }}
        onChanged={refresh}
      />
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && finishEditing()}
        title={editing === 'new' ? 'Agendar reunião' : 'Editar reunião'}
        className="w-[min(44rem,calc(100vw-2rem))]"
      >
        {editing === null ? null : options.isPending ? (
          <LoadingState rows={3} label="Carregando participantes, áreas e unidades" />
        ) : options.isError ? (
          <ErrorState
            title="Não foi possível carregar participantes, áreas e unidades"
            error={options.error}
            onRetry={() => void options.refetch()}
            retrying={options.isFetching}
          />
        ) : (
          <MeetingForm
            base={base}
            initial={editing === 'new' ? null : editing}
            options={options.data}
            onSaved={async (m) => {
              finishEditing();
              toast.success(editing === 'new' ? 'Reunião agendada' : 'Reunião alterada', {
                description: m.title,
              });
              await refresh();
            }}
            onCancel={finishEditing}
          />
        )}
      </Dialog>
    </section>
  );
}

/** Calendário do mês (segunda a domingo); no celular, a lista dos dias com reunião. */
function MeetingCalendar({
  base,
  projectId,
  onOpen,
  type,
}: {
  base: string;
  projectId: string;
  onOpen: (id: string) => void;
  /** Só as reuniões deste tipo (a tela de Análise crítica). */
  type?: MeetingTypeName;
}) {
  const { client } = useAuth();
  const today = localParts(new Date().toISOString()).day;
  const [ym, setYm] = useState(() => ({ y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) }));
  const range = monthRange(ym.y, ym.m);
  const month = useQuery({
    queryKey: ['meetings', projectId, 'month', ym.y, ym.m, type ?? 'all'],
    queryFn: ({ signal }) =>
      client.request(
        `${base}?${new URLSearchParams({
          from: range.from,
          to: range.to,
          pageSize: '100',
          order: 'asc',
          ...(type ? { type } : {}),
        })}`,
        { schema: meetingListSchema, signal },
      ),
    placeholderData: keepPreviousData,
  });
  const byDay = useMemo(() => {
    const map = new Map<string, Meeting[]>();
    for (const m of month.data?.items ?? []) {
      const { day } = localParts(m.startsAt);
      map.set(day, [...(map.get(day) ?? []), m]);
    }
    return map;
  }, [month.data]);
  const step = (delta: number) =>
    setYm(({ y, m }) => {
      const total = y * 12 + (m - 1) + delta;
      return { y: Math.floor(total / 12), m: (total % 12) + 1 };
    });
  const label = `${MONTH_NAMES[ym.m - 1]} de ${ym.y}`;
  const weeks = monthGrid(ym.y, ym.m);
  const daysWith = [...byDay.keys()].sort();

  return (
    <section aria-label={`Calendário de reuniões: ${label}`} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="icon-sm" aria-label="Mês anterior" onClick={() => step(-1)}>
          <ChevronLeft aria-hidden />
        </Button>
        <h2 className="text-base font-bold first-letter:uppercase" aria-live="polite">
          {label}
          {month.isFetching ? <span className="sr-only"> (carregando)</span> : null}
        </h2>
        <Button variant="outline" size="icon-sm" aria-label="Próximo mês" onClick={() => step(1)}>
          <ChevronRight aria-hidden />
        </Button>
      </div>
      {month.isPending ? (
        <LoadingState rows={4} label="Carregando o calendário" />
      ) : month.isError ? (
        <ErrorState error={month.error} onRetry={() => void month.refetch()} retrying={month.isFetching} />
      ) : (
        <>
          {/* Desktop e tablet: grade do mês. */}
          <table className="hidden w-full table-fixed border-collapse text-sm md:table">
            <caption className="sr-only">Reuniões de {label}</caption>
            <thead>
              <tr>
                {WEEKDAYS.map((d) => (
                  <th key={d} scope="col" className="pb-1 text-xs font-semibold text-muted-foreground">
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, i) => (
                <tr key={i}>
                  {week.map((day, j) => (
                    <td
                      key={j}
                      className={cn(
                        'h-24 border border-border p-1 align-top',
                        !day && 'bg-muted/40',
                        day === today && 'bg-brand-soft/40',
                      )}
                    >
                      {day ? (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-semibold text-muted-foreground">
                            {Number(day.slice(8))}
                          </span>
                          {(byDay.get(day) ?? []).map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => onOpen(m.id)}
                              className={cn(
                                'truncate rounded-sm px-1 py-0.5 text-left text-xs focus-visible:outline-2 focus-visible:outline-ring',
                                m.pending
                                  ? 'bg-status-red-bg text-status-red-fg'
                                  : m.status === 'CLOSED'
                                    ? 'bg-muted text-muted-foreground'
                                    : 'bg-brand-soft text-brand-strong dark:text-brand',
                              )}
                              aria-label={`${m.title}, ${when(m)}${m.pending ? ', pendente' : ''}`}
                            >
                              {localParts(m.startsAt).time} {m.title}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {/* Celular: os dias com reunião, em lista. */}
          <div className="md:hidden">
            {daysWith.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                Nenhuma reunião em {label}.
              </p>
            ) : (
              <ol aria-label={`Dias com reunião em ${label}`} className="flex flex-col gap-3">
                {daysWith.map((day) => (
                  <li key={day} className="rounded-lg border border-border p-3">
                    <h3 className="text-sm font-bold">{formatDate(`${day}T12:00:00Z`)}</h3>
                    <ul className="mt-2 flex flex-col gap-1">
                      {byDay.get(day)!.map((m) => (
                        <li key={m.id}>
                          <button
                            type="button"
                            onClick={() => onOpen(m.id)}
                            aria-label={`${m.title}, ${when(m)}${m.pending ? ', pendente' : ''}`}
                            className="flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-surface focus-visible:outline-2 focus-visible:outline-ring"
                          >
                            <span className="tabular-nums text-muted-foreground">
                              {localParts(m.startsAt).time}
                            </span>
                            <span className="min-w-0 flex-1 truncate font-medium">{m.title}</span>
                            {statusOf(m)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
          </div>
          {month.data.total > month.data.items.length ? (
            <p className="text-xs text-muted-foreground">
              Mostrando {month.data.items.length} de {month.data.total} reuniões do mês: use a lista para ver
              todas.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

type Tab = 'details' | 'agenda' | 'collab' | 'minutes';

/** Erros do servidor nos campos da próxima reunião (next.title, next.startsAt). */
const nextErrorsOf = (err: unknown): FieldErrors =>
  err instanceof ApiError
    ? Object.fromEntries(
        err.details.filter((d) => d.path.startsWith('next.')).map((d) => [d.path, d.message]),
      )
    : {};

/**
 * Detalhe: dados, participantes, pauta e registro; iniciar, registrar, encerrar, editar e excluir. Na análise
 * crítica, a pauta montada e a condução (T-121c); em toda reunião, comentários e anexos (T-121b; DEC-047).
 */
function MeetingDetail({
  base,
  organizationId,
  projectId,
  meetingId,
  readOnly,
  canDelete,
  canCreate,
  onOpenMeeting,
  drafts,
  onClose,
  onEdit,
  onChanged,
}: {
  base: string;
  organizationId: string;
  projectId: string;
  meetingId: string | null;
  readOnly: boolean;
  canDelete: boolean;
  /** "Criar" em Reuniões: pode informar a próxima reunião ao encerrar. */
  canCreate: boolean;
  onOpenMeeting: (id: string) => void;
  drafts: Map<string, string>;
  onClose: () => void;
  onEdit: (m: Meeting) => void;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const recordId = useId();
  const tabsId = useId();
  const [tab, setTab] = useState<Tab>('details');
  // Abas já visitadas ficam montadas (escondidas): o que foi digitado na condução ou num comentário não se perde.
  const [seen, setSeen] = useState<ReadonlySet<Tab>>(() => new Set<Tab>(['details']));
  useEffect(() => {
    setTab('details');
    setSeen(new Set<Tab>(['details']));
  }, [meetingId]);
  const showTab = (next: Tab) => {
    setTab(next);
    setSeen((prev) => new Set(prev).add(next));
  };
  const detail = useQuery({
    // Sob a chave do projeto: agendar, editar e mudar status atualizam o detalhe junto com a lista.
    queryKey: ['meetings', projectId, 'detail', meetingId],
    queryFn: ({ signal }) => client.request(`${base}/${meetingId}`, { schema: meetingSchema, signal }),
    enabled: meetingId !== null,
  });
  const [record, setRecord] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [removing, setRemoving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [nextErrors, setNextErrors] = useState<FieldErrors>({});
  const m = detail.data;
  const shownId = m?.id;
  const savedRecord = m?.record;
  useEffect(() => {
    setRecord((shownId ? drafts.get(shownId) : undefined) ?? savedRecord ?? '');
    setErrors({});
  }, [shownId, savedRecord, drafts]);
  const changeRecord = (value: string) => {
    setRecord(value);
    if (m) drafts.set(m.id, value);
  };

  const after = async (updated: Meeting, message: string) => {
    drafts.delete(updated.id);
    toast.success(message, { description: updated.title });
    await Promise.all([detail.refetch(), onChanged()]);
  };
  const status = useMutation({
    meta: { handlesError: true },
    mutationFn: (body: {
      status: 'IN_PROGRESS' | 'CLOSED';
      record?: string;
      next?: NextMeetingInput | null;
    }) => client.request(`${base}/${meetingId}/status`, { method: 'POST', body, schema: meetingSchema }),
    onSuccess: async (updated) => {
      if (updated.status === 'CLOSED') {
        setClosing(false);
        setNextErrors({});
        await after(updated, 'Reunião encerrada e ata gerada');
        if (updated.minutesAt) showTab('minutes');
      } else {
        await after(updated, 'Reunião iniciada');
      }
    },
    onError: (err) => {
      // Erro na próxima reunião: fica no diálogo do encerramento, no campo.
      const next = nextErrorsOf(err);
      if (hasFieldErrors(next)) return setNextErrors(next);
      setClosing(false);
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else toast.error('Não foi possível mudar a reunião', { description: errorMessage(err) });
    },
  });
  const save = useMutation({
    meta: { handlesError: true },
    mutationFn: (value: string) =>
      client.request(`${base}/${meetingId}/record`, {
        method: 'PUT',
        body: { record: value },
        schema: meetingSchema,
      }),
    onSuccess: (updated) => after(updated, 'Registro salvo'),
    onError: (err) => {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else toast.error('Não foi possível salvar o registro', { description: errorMessage(err) });
    },
  });
  const remove = useMutation({
    meta: { handlesError: true },
    mutationFn: () => client.request(`${base}/${meetingId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(false);
      toast.success('Reunião excluída');
      onClose();
      await onChanged();
    },
    onError: (err) => toast.error('Não foi possível excluir', { description: errorMessage(err) }),
  });
  // Encerrar: o registro é exigido; depois, o diálogo com a próxima reunião (DEC-051).
  const close = () => {
    const parsed = meetingStatusInputSchema.safeParse({ status: 'CLOSED', record });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setNextErrors({});
    setClosing(true);
  };
  const confirmClose = (next: NextMeetingInput | null | undefined) =>
    status.mutate({ status: 'CLOSED', record: record.trim(), ...(next === undefined ? {} : { next }) });
  const busy = status.isPending || save.isPending || remove.isPending;

  return (
    <Dialog
      open={meetingId !== null}
      onOpenChange={(open) => !open && onClose()}
      busy={busy}
      title={m?.title ?? 'Reunião'}
      className={cn(
        'w-[min(44rem,calc(100vw-2rem))]',
        m?.type === 'CRITICAL_ANALYSIS' && 'w-[min(60rem,calc(100vw-2rem))] max-h-[min(92dvh,60rem)]',
      )}
    >
      {detail.isPending ? (
        <LoadingState rows={3} label="Carregando a reunião" />
      ) : detail.isError ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} retrying={detail.isFetching} />
      ) : m ? (
        <div className="flex flex-col gap-4 text-sm">
          <TabList
            id={tabsId}
            label="Seções da reunião"
            tabs={[
              { key: 'details', label: 'Reunião' },
              ...(m.type === 'CRITICAL_ANALYSIS'
                ? [{ key: 'agenda' as const, label: 'Pauta e condução' }]
                : []),
              { key: 'collab', label: 'Comentários e anexos' },
              ...(m.minutesAt ? [{ key: 'minutes' as const, label: 'Ata' }] : []),
            ]}
            value={tab}
            onChange={showTab}
          />
          {m.type === 'CRITICAL_ANALYSIS' && seen.has('agenda') ? (
            <div {...tabPanelProps(tabsId, 'agenda')} hidden={tab !== 'agenda'}>
              <CriticalAnalysisPanel
                organizationId={organizationId}
                projectId={projectId}
                meetingId={m.id}
                meetingStatus={m.status}
                readOnly={readOnly}
              />
            </div>
          ) : null}
          {m.minutesAt && seen.has('minutes') ? (
            <div {...tabPanelProps(tabsId, 'minutes')} hidden={tab !== 'minutes'}>
              <MeetingMinutes
                organizationId={organizationId}
                projectId={projectId}
                meetingId={m.id}
                onOpenMeeting={onOpenMeeting}
              />
            </div>
          ) : null}
          {seen.has('collab') ? (
            <div {...tabPanelProps(tabsId, 'collab')} hidden={tab !== 'collab'}>
              <CollaborationPanel
                organizationId={organizationId}
                projectId={projectId}
                subject="meeting"
                subjectId={m.id}
                label={m.title}
              />
            </div>
          ) : null}
          <div
            {...tabPanelProps(tabsId, 'details')}
            hidden={tab !== 'details'}
            className="flex flex-col gap-4"
          >
            <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-semibold uppercase">{MEETING_TYPE_LABELS[m.type]}</span>
              <span>{MEETING_LEVEL_LABELS[m.level]}</span>
              {statusOf(m)}
            </p>
            <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[max-content_1fr]">
              <dt className="font-semibold">Quando</dt>
              <dd>{when(m)}</dd>
              {m.department || m.businessUnit ? (
                <>
                  <dt className="font-semibold">Área / unidade</dt>
                  <dd>{[m.department?.name, m.businessUnit?.name].filter(Boolean).join(' · ')}</dd>
                </>
              ) : null}
              <dt className="font-semibold">Participantes</dt>
              <dd>
                {m.participants.length ? (
                  <ul aria-label="Participantes" className="flex flex-wrap gap-1.5">
                    {m.participants.map((p) => (
                      <li key={p.id} className="rounded-full bg-muted px-2 py-0.5">
                        {p.name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted-foreground">Ninguém convidado ainda.</span>
                )}
              </dd>
              <dt className="font-semibold">Agendada por</dt>
              <dd>{m.createdByName}</dd>
            </dl>
            <div>
              <h3 className="text-sm font-bold">Pauta</h3>
              <p className="mt-1 whitespace-pre-line break-words text-muted-foreground">
                {m.agenda ?? 'Sem pauta registrada.'}
              </p>
            </div>
            {m.pending ? (
              <p role="status" className="rounded-md bg-status-red-bg px-3 py-2 text-status-red-fg">
                O horário desta reunião já passou e ela não foi iniciada: inicie para registrar o que foi
                tratado ou edite para remarcar.
              </p>
            ) : null}
            {m.status === 'SCHEDULED' ? null : (
              <div>
                {m.status === 'IN_PROGRESS' && m.canEdit ? (
                  <FormField id={recordId} label="Registro do que foi tratado" error={errors.record}>
                    <textarea
                      {...fieldA11y(recordId, errors.record)}
                      value={record}
                      rows={6}
                      maxLength={MEETING_TEXT_MAX}
                      onChange={(e) => changeRecord(e.target.value)}
                      className={textareaClass}
                      placeholder="Assuntos, encaminhamentos e próximos passos"
                    />
                  </FormField>
                ) : (
                  <>
                    <h3 className="text-sm font-bold">Registro</h3>
                    <p className="mt-1 whitespace-pre-line break-words">
                      {m.record ?? 'Sem registro ainda.'}
                    </p>
                  </>
                )}
              </div>
            )}
            {m.canEdit ? (
              <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
                {/* Excluir: só a agendada, com a permissão e no escopo (canEdit já traz o escopo — DEC-049). */}
                {canDelete && m.status === 'SCHEDULED' ? (
                  <Button variant="ghost" disabled={busy} onClick={() => setRemoving(true)}>
                    Excluir
                  </Button>
                ) : null}
                {/* Editar vale até encerrar (incluir participante ou ajustar a pauta durante a reunião). */}
                <Button variant="outline" disabled={busy} onClick={() => onEdit(m)}>
                  Editar
                </Button>
                {m.status === 'SCHEDULED' ? (
                  <>
                    <Button
                      loading={status.isPending}
                      disabled={busy}
                      onClick={() => status.mutate({ status: 'IN_PROGRESS' })}
                    >
                      Iniciar reunião
                    </Button>
                  </>
                ) : null}
                {m.status === 'IN_PROGRESS' ? (
                  <>
                    <Button
                      variant="outline"
                      loading={save.isPending}
                      disabled={busy}
                      onClick={() => save.mutate(record)}
                    >
                      Salvar registro
                    </Button>
                    <Button loading={status.isPending} disabled={busy} onClick={close}>
                      Encerrar reunião
                    </Button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {m ? (
        <CloseMeetingDialog
          // Reunião editada (título ou horário): a sugestão da próxima recomeça a partir dela.
          key={`${m.id}:${m.startsAt}:${m.title}`}
          meeting={m}
          open={closing}
          canSchedule={canCreate}
          busy={status.isPending}
          serverErrors={nextErrors}
          onConfirm={confirmClose}
          onCancel={() => setClosing(false)}
        />
      ) : null}
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title="Excluir a reunião?"
        description="A reunião agendada sai da agenda. A trilha de auditoria guarda o registro."
        confirmLabel="Excluir"
        destructive
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </Dialog>
  );
}

const DURATIONS = [30, 45, 60, 90, 120, 180, 240];

/** Agendar ou editar: tipo, nível, título, data e hora, duração, área/unidade, participantes e pauta. */
function MeetingForm({
  base,
  initial,
  options,
  onSaved,
  onCancel,
}: {
  base: string;
  initial: Meeting | null;
  options: MeetingOptions;
  onSaved: (m: Meeting) => Promise<void>;
  onCancel: () => void;
}) {
  const { client } = useAuth();
  const start = initial ? localParts(initial.startsAt) : null;
  const [type, setType] = useState<MeetingTypeName>(initial?.type ?? 'CRITICAL_ANALYSIS');
  const [level, setLevel] = useState<MeetingLevelName>(initial?.level ?? 'BOARD');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [day, setDay] = useState(start?.day ?? '');
  const [time, setTime] = useState(start?.time ?? '09:00');
  const [duration, setDuration] = useState(String(initial?.durationMinutes ?? 60));
  const [departmentId, setDepartmentId] = useState(
    initial?.department?.id ?? (options.departments.length === 1 ? options.departments[0]!.id : ''),
  );
  const [businessUnitId, setBusinessUnitId] = useState(initial?.businessUnit?.id ?? '');
  const [participantIds, setParticipantIds] = useState<string[]>(
    initial?.participants.map((p) => p.id) ?? [],
  );
  const [agenda, setAgenda] = useState(initial?.agenda ?? '');
  const [filter, setFilter] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = {
    type: useId(),
    level: useId(),
    title: useId(),
    day: useId(),
    time: useId(),
    duration: useId(),
    department: useId(),
    unit: useId(),
    filter: useId(),
    agenda: useId(),
  };
  // Participante que já saiu da lista de opções (inativo) continua aparecendo enquanto estiver marcado.
  const people = useMemo(() => {
    const known = new Map(options.people.map((p) => [p.id, p]));
    for (const p of initial?.participants ?? []) {
      if (!known.has(p.id)) known.set(p.id, { ...p, fluxus: false });
    }
    return [...known.values()];
  }, [options.people, initial]);
  const shown = people.filter((p) => p.name.toLowerCase().includes(filter.trim().toLowerCase()));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const payload = {
      type,
      level,
      title,
      startsAt: day && time ? startsAtFrom(day, time) : '',
      durationMinutes: duration,
      departmentId: departmentId || null,
      businessUnitId: businessUnitId || null,
      participantIds,
      agenda,
    };
    const parsed = meetingInputSchema.safeParse(payload);
    if (!parsed.success) {
      const fields = zodFieldErrors(parsed.error);
      // A data e a hora viram um campo só na API: o erro aparece no campo da data.
      if (fields.startsAt) fields.day = fields.startsAt;
      return setErrors(fields);
    }
    setErrors({});
    setFormError(null);
    setSaving(true);
    try {
      const saved = await client.request(initial ? `${base}/${initial.id}` : base, {
        method: initial ? 'PUT' : 'POST',
        body: parsed.data,
        schema: meetingSchema,
      });
      await onSaved(saved);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (fields.startsAt) fields.day = fields.startsAt;
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField id={ids.type} label="Tipo" error={errors.type}>
          <NativeSelect
            {...fieldA11y(ids.type, errors.type)}
            value={type}
            onChange={(e) => setType(e.target.value as MeetingTypeName)}
          >
            {MEETING_TYPES.map((t) => (
              <option key={t} value={t}>
                {MEETING_TYPE_LABELS[t]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.level} label="Nível" error={errors.level}>
          <NativeSelect
            {...fieldA11y(ids.level, errors.level)}
            value={level}
            onChange={(e) => setLevel(e.target.value as MeetingLevelName)}
          >
            {MEETING_LEVELS.map((l) => (
              <option key={l} value={l}>
                {MEETING_LEVEL_LABELS[l]}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      <FormField id={ids.title} label="Título" error={errors.title}>
        <Input
          {...fieldA11y(ids.title, errors.title)}
          value={title}
          maxLength={MEETING_TITLE_MAX}
          placeholder="Ex.: Análise crítica de março"
          onChange={(e) => setTitle(e.target.value)}
          className="h-11"
        />
      </FormField>
      <div className="grid gap-3 sm:grid-cols-3">
        <FormField id={ids.day} label="Data" error={errors.day}>
          <Input
            {...fieldA11y(ids.day, errors.day)}
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.time} label="Hora" error={errors.time}>
          <Input
            {...fieldA11y(ids.time, errors.time)}
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="h-11"
          />
        </FormField>
        <FormField id={ids.duration} label="Duração" error={errors.durationMinutes}>
          <NativeSelect
            {...fieldA11y(ids.duration, errors.durationMinutes)}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          >
            {[...new Set([...DURATIONS, Number(duration)])]
              .filter((d) => d >= MEETING_DURATION_MIN && d <= MEETING_DURATION_MAX)
              .sort((a, b) => a - b)
              .map((d) => (
                <option key={d} value={String(d)}>
                  {d < 60 ? `${d} min` : d % 60 ? `${Math.floor(d / 60)} h ${d % 60} min` : `${d / 60} h`}
                </option>
              ))}
          </NativeSelect>
        </FormField>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField id={ids.department} label="Área" error={errors.departmentId}>
          <NativeSelect
            {...fieldA11y(ids.department, errors.departmentId)}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
          >
            <option value="">Nenhuma (empresa toda)</option>
            {options.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.unit} label="Unidade" error={errors.businessUnitId}>
          <NativeSelect
            {...fieldA11y(ids.unit, errors.businessUnitId)}
            value={businessUnitId}
            onChange={(e) => setBusinessUnitId(e.target.value)}
          >
            <option value="">Nenhuma</option>
            {options.businessUnits.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      <fieldset
        className="flex flex-col gap-2"
        aria-describedby={errors.participantIds ? `${ids.filter}-erro` : undefined}
      >
        <legend className="text-sm font-medium">
          Participantes{participantIds.length ? ` (${participantIds.length})` : ''}
        </legend>
        <Input
          id={ids.filter}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Buscar pelo nome"
          aria-label="Buscar participante pelo nome"
          className="h-10"
        />
        <ul className="flex max-h-56 flex-col overflow-y-auto rounded-md border border-border">
          {shown.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">Ninguém com esse nome.</li>
          ) : (
            shown.map((p) => {
              const checked = participantIds.includes(p.id);
              return (
                <li key={p.id}>
                  <label className="flex min-h-11 cursor-pointer items-center gap-2 px-3 text-sm hover:bg-surface">
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 accent-(--brand)"
                      checked={checked}
                      onChange={() =>
                        setParticipantIds((list) =>
                          checked ? list.filter((x) => x !== p.id) : [...list, p.id],
                        )
                      }
                    />
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    {p.fluxus ? <span className="text-xs text-muted-foreground">Equipe Fluxus</span> : null}
                  </label>
                </li>
              );
            })
          )}
        </ul>
        {errors.participantIds ? (
          <p id={`${ids.filter}-erro`} className="text-sm font-medium text-status-red-fg">
            {errors.participantIds}
          </p>
        ) : null}
      </fieldset>
      <FormField id={ids.agenda} label="Pauta (opcional)" error={errors.agenda}>
        <textarea
          {...fieldA11y(ids.agenda, errors.agenda)}
          value={agenda}
          rows={4}
          maxLength={MEETING_TEXT_MAX}
          placeholder="Assuntos a tratar, um por linha"
          onChange={(e) => setAgenda(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar alterações' : 'Agendar'}
        </Button>
      </div>
    </form>
  );
}
